/**
 * navigation-coordinator.ts
 *
 * Unified navigation coordination for MFE ↔ CC shell communication.
 *
 * All shell-to-MFE navigation — event-based (CTA/menu clicks) AND
 * property-based (deep-link bootstrap) — flows through this coordinator.
 *
 *   1. Tracks whether an event-based navigation arrived this mount
 *      (bootstrap deep-link driver defers when true)
 *   2. Tracks a single pending shell-driven navigation for onSuccess
 *      echo suppression (prevents CC routeSync issues)
 *   3. Suppresses intermediate dashboard publishes during deep-link redirects
 *   4. Resets cleanly on each mount — no stale state across remounts
 *
 * Design: no CTA-vs-bootstrap "authority" concept — just "did an event arrive?"
 * No retry-on-supersede — proper coordination prevents the race.
 * Generation counter rejects stale matches across mounts.
 */

export interface PendingShellNavigation {
    stateName: string;
    params: Record<string, unknown>;
    generation: number;
    seq: number;
}

interface CoordinatorState {
    eventNavigationReceived: boolean;
    pendingShellNav: PendingShellNavigation | null;
    generation: number;
    seq: number;
    suppressDashboardPublish: boolean;
}

const state: CoordinatorState = {
    eventNavigationReceived: false,
    pendingShellNav: null,
    generation: 0,
    seq: 0,
    suppressDashboardPublish: false,
};

// --- Mount lifecycle ---

export function resetNavigationCoordinator (): void {
    state.eventNavigationReceived = false;
    state.pendingShellNav = null;
    state.generation++;
    state.suppressDashboardPublish = false;
}

// --- Event navigation tracking ---

export function markEventNavigationReceived (): void {
    state.eventNavigationReceived = true;
}

export function hasEventNavigation (): boolean {
    return state.eventNavigationReceived;
}

// --- Pending shell navigation (echo suppression) ---

export function setPendingShellNavigation (
    stateName: string,
    params: Record<string, unknown>,
): number {
    const seq = ++state.seq;
    state.pendingShellNav = {
        stateName,
        params,
        generation: state.generation,
        seq,
    };
    return seq;
}

export function getPendingShellNavigation (): PendingShellNavigation | null {
    if (state.pendingShellNav && state.pendingShellNav.generation !== state.generation) {
        state.pendingShellNav = null;
    }
    return state.pendingShellNav;
}

/**
 * Check whether a completing transition matches the pending shell navigation
 * and should be suppressed (not echoed to CC). Consumes the pending record
 * on match.
 */
export function consumePendingShellNavigation (
    completedStateName: string,
    completedParams: Record<string, unknown>,
): boolean {
    const pending = state.pendingShellNav;
    if (pending?.generation !== state.generation) {
        state.pendingShellNav = null;
        return false;
    }

    if (completedStateName !== pending.stateName) {
        return false;
    }

    for (const key of Object.keys(pending.params)) {
        if (String(pending.params[key]) !== String((completedParams)[key])) {
            return false;
        }
    }

    state.pendingShellNav = null;
    return true;
}

export function clearPendingShellNavigationBySeq (seq: number): void {
    if (state.pendingShellNav?.seq === seq) {
        state.pendingShellNav = null;
    }
}

// --- Dashboard publish suppression ---

export function setSuppressDashboardPublish (suppress: boolean): void {
    state.suppressDashboardPublish = suppress;
}

export function shouldSuppressDashboardPublish (stateName: string): boolean {
    return state.suppressDashboardPublish && stateName === 'base.customer.dashboard';
}

export function clearDashboardPublishSuppression (): void {
    state.suppressDashboardPublish = false;
}

// --- Test support ---

export function _resetNavigationCoordinator (): void {
    state.eventNavigationReceived = false;
    state.pendingShellNav = null;
    state.generation = 0;
    state.seq = 0;
    state.suppressDashboardPublish = false;
}
